import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import {COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileViewQuery, FileViewQueryVariables} from 'files/workspace/__generated/FileViewQuery';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import FileDetails from './FileDetails';

interface FileViewProps {
  id: string;
}

export const FILE_VIEW_QUERY: DocumentNode = gql`
  query FileViewQuery($id: StrictString!) @skipAudit @engComponent(name: PIPELINES) {
    file(id: $id) {
      ... on FileNode {
        id
        name
        createdAt
        modifiedAt
        displayId
        originType
        url
        scratchFile {
          id
          name
          downloadUrl
          filename
          externalFileId
          size
          mimeType
          uploadStatus
        }
        lineageParent {
          ... on ConnectionNode {
            id
          }
          ... on AnalysisNode {
            id
          }
          ... on StudyNode {
            id
          }
          ... on RunNode {
            id
            apiId
          }
          ... on AutomationOutputFileNode {
            id
          }
          ... on PredictionBatchNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        creator {
          id
          name
          handle
          avatarUrl
        }
        folder {
          id
        }
        permissions {
          readable
          writable
          archivable
          unarchivable
        }
        customFields {
          name
          value
        }
        ...CoreObjectArchivableGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function FileView({id}: FileViewProps): JSX.Element | null {
  const result = useBenchlingQuery<FileViewQuery, FileViewQueryVariables>(FILE_VIEW_QUERY, {
    variables: {id},
  });

  useOnMount(() => {
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.VIEWED_WORKSPACE_ITEM,
      ANALYTICS_MODULE.INSIGHTS,
      {
        event_group_id: Analytics.makeAnalyticsEventId(),
        action_origin: ActionOrigin.UI_UNDETERMINED,
        viewed_object_id: id,
        viewed_object_type: 'file',
      }
    );
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    if (isInaccessible(data.file)) {
      return (
        <WorkspaceItemInaccessibleGuard inaccessibleInfo={data.file} modelType={ModelTypeConstants.FILE} />
      );
    } else {
      const reload = (): void => {
        result.refetch({id});
      };
      return <FileDetails file={data.file} reload={reload} />;
    }
  } else {
    return null;
  }
}
